import i18n, { i18n_t } from '@/locales/i18n';
import { SyncOperationType } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/SyncOperationItem';
import { LocalTreeNode, RemoteTreeNode } from '@/workerProcessRenderer/scripts/CoSyncEngine/Tree/typings';
import { ConflictDataObj, conflictResolveType } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/ConflictData';
import { InvalidFilenameInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/invalidFilename';

export enum CoSyncEvent {
  EVENT_SYNC_STATUS_CHANGE = 'EVENT_SYNC_STATUS_CHANGE', // 同步盘状态更新

  EVENT_OPERATE_STATUS_CHANGE = 'EVENT_OPERATE_STATUS_CHANGE', // 执行器任务状态通知

  EVENT_MESSAGE_TO_RENDERER = 'EVENT_MESSAGE_TO_RENDERER', // 给renderer发消息
}

export enum CoSyncStatus {
  STATUS_IDLE = 'STATUS_IDLE',
  STATUS_WAIT_FOR_RUNNING = 'STATUS_WAIT_FOR_RUNNING',
  STATUS_RUNNING = 'STATUS_RUNNING',
  STATUS_STOP = 'STATUS_STOP',
  STATUS_SUCCESS = 'STATUS_SUCCESS',
  STATUS_ERROR = 'STATUS_ERROR', // 错误
  STATUS_WARN = 'STATUS_WARN', // 告警
  STATUS_CONFLICT = 'STATUS_CONFLICT', // 冲突
}

export enum CoSyncRunningSubStatus {
  STATUS_RUNNING_SCANNER_START = 'STATUS_RUNNING_SCANNER_START',
  STATUS_RUNNING_PLANNER_START = 'STATUS_RUNNING_PLANNER_START',
  STATUS_RUNNING_OPERATE_START = 'STATUS_RUNNING_OPERATE_START',
}

export interface SyncBaseStatusInfo {
  syncId: number;
  status: CoSyncStatus;
}

export interface SyncStatusRunningInfo extends SyncBaseStatusInfo {
  subStatus: CoSyncRunningSubStatus;
}

export interface SyncStatusResultInfo extends SyncBaseStatusInfo {
  result: CoSyncResultCode;
  error?: string;
  finishTime: number; // 本地同步树冻结时间作为完成时间
  errorTaskNum?: number; // 只有STATUS_SUCCESS或者STATUS_WARN才有值
  skippedFiles?: InvalidFilenameInfo[];
}

export interface SyncStatusConflictInfo {
  syncId: number;
  status: CoSyncStatus;
  conflictData: ConflictDataObj;
}

export type CoSynStatusInfo =
  | SyncBaseStatusInfo
  | SyncStatusResultInfo
  | SyncStatusConflictInfo
  | SyncStatusRunningInfo;

export enum CoSyncResultCode {
  CODE_SUCCESS = 'CODE_SUCCESS', // 成功
  CODE_FAIL = 'CODE_FAIL', // 失败
  CODE_CREATE_TASK_FAIL = 'CODE_CREATE_TASK_FAIL', // 创建任务失败
  CODE_PLANNER_FAIL = 'CODE_PLANNER_FAIL', // 规划器失败
  CODE_SCANNER_FAIL = 'CODE_SCANNER_FAIL', // 扫描器失败
  CODE_SCANNER_WARNING = 'CODE_SCANNER_WARNING', // 扫描器告警
  CODE_OPERATE_FORMAT_FAIL = 'CODE_OPERATE_FORMAT_FAIL', // 执行任务转换失败
  CODE_SCANNER_GET_ROOT_PATH_ERROR = 'CODE_SCANNER_GET_ROOT_PATH_ERROR', // 扫描器获取根路径失败
  CODE_WARN = 'CODE_WARN', // 告警错误
  CODE_PARAM_ERROR = 'CODE_PARAM_ERROR', // 参数错误
  CODE_CREATE_FOLDER_PARAM_ERROR = 'CODE_CREATE_FOLDER_PARAM_ERROR', // 创建文件夹参数错误
  CODE_CREATE_FOLDER_ERROR = 'CODE_CREATE_FOLDER_ERROR', // 创建文件夹失败
  CODE_STOP_ERROR = 'CODE_STOP_ERROR', // 停止导致的错误
  CODE_DELETE_REMOTE_FILE_ERROR = 'CODE_DELETE_REMOTE_FILE_ERROR', // 删除远端文件错误
  CODE_DELETE_REMOTE_DIR_ERROR = 'CODE_DELETE_REMOTE_DIR_ERROR', // 删除远端文件夹错误
  CODE_DELETE_FOLDER_HAVE_FILE_ERROR = 'CODE_DELETE_FOLDER_HAVE_FILE_ERROR', // 删除文件夹有文件错误
  CODE_DELETE_FILE_SIZE_CHANGE = 'CODE_DELETE_FILE_SIZE_CHANGE', // 删除文件时文件大小发生变化错误
  CODE_DELETE_FILE_MTIME_CHANGE = 'CODE_DELETE_FILE_MTIME_CHANGE', // 删除文件时文件修改时间发生变化错误
  CODE_DELETE_FILE_INODE_CHANGE = 'CODE_DELETE_FILE_INODE_CHANGE', // 删除文件时文件标识发生变化错误
  CODE_MOVE_FOLDER_DST_IS_FILE = 'CODE_MOVE_FOLDER_DST_IS_FILE', // 移动文件夹时目标路径已经存在文件
  CODE_CREAT_FOLDER_FAIL = 'CODE_CREAT_FOLDER_FAIL', // 创建文件夹失败
  CODE_DOWNLOAD_FILE_LOCAL_CHANGE = 'CODE_DOWNLOAD_FILE_LOCAL_CHANGE', // 下载时本地文件发生变更
  CODE_BASE_PARAM_ERROR = 'CODE_BASE_PARAM_ERROR', // 基础参数错误
  CODE_MOVE_REMOTE_FILE_ERROR = 'CODE_MOVE_REMOTE_FILE_ERROR', // 移动远程文件错误
  CODE_DELETE_FOLDER_IS_FILE = 'CODE_DELETE_FOLDER_IS_FILE', // 删除文件夹时路径为文件
  CODE_REMOTE_REQUEST_FAIL = 'CODE_REMOTE_REQUEST_FAIL', // 请求出错
  CODE_DIFF_LIST_ERROR = 'CODE_DIFF_LIST_ERROR', // 获取规划器差异列表失败
  CODE_SSN_NO_MATCH = 'CODE_SSN_NO_MATCH', // 存在文件冲突，本次无法上传
  CODE_DELETE_REMOTE_FOLDER_HAVE_FILE_ERROR = 'CODE_DELETE_REMOTE_FOLDER_HAVE_FILE_ERROR', // 删除远端文件夹有文件错误
  CODE_MOVE_LOCAL_SRC_NO_EXIST_ERROR = 'CODE_MOVE_LOCAL_SRC_NO_EXIST_ERROR', // 源路径不存在
  CODE_MOVE_LOCAL_CREATE_DIR_ERROR = 'CODE_MOVE_LOCAL_CREATE_DIR_ERROR', // 创建目录失败
  CODE_DELETE_DIR_NO_EMPTY_ERROR = 'CODE_PATH_NO_EMPTY_ERROR', // 删除目录非空
  CODE_GET_LOCAL_PATH_INFO_ERROR = 'CODE_GET_LOCAL_PATH_INFO_ERROR', // 获取本地文件信息失败
  CODE_DOWNLOAD_GET_INFO_ERROR = 'CODE_DOWNLOAD_GET_INFO_ERROR', // 下载获取云端文件信息失败
  CODE_NO_PERMISSION_ERROR = 'CODE_NO_PERMISSION_ERROR', // 没有权限
  CODE_SYNC_TASK_NOT_FOUND_ERROR = 'CODE_SYNC_TASK_NOT_FOUND_ERROR', // 同步任务不存在或者已被移除
}

export function isFatalError(code: CoSyncResultCode): boolean {
  let ret = false;
  switch (code) {
    case CoSyncResultCode.CODE_CREATE_TASK_FAIL:
    case CoSyncResultCode.CODE_PLANNER_FAIL:
    case CoSyncResultCode.CODE_SCANNER_FAIL:
    case CoSyncResultCode.CODE_OPERATE_FORMAT_FAIL:
    case CoSyncResultCode.CODE_PARAM_ERROR:
    case CoSyncResultCode.CODE_DIFF_LIST_ERROR:
    case CoSyncResultCode.CODE_NO_PERMISSION_ERROR:
    case CoSyncResultCode.CODE_SYNC_TASK_NOT_FOUND_ERROR:
      ret = true;
      break;
  }

  return ret;
}

const ResultErrorTextMap: Record<string, string> = {
  CODE_SUCCESS: i18n_t('权限被拒绝'),
  CODE_FAIL: i18n_t('同步错误'),
  CODE_CREATE_TASK_FAIL: i18n_t('创建任务错误'),
  CODE_PLANNER_FAIL: i18n_t('同步规划异常'),
  CODE_SCANNER_FAIL: i18n_t('同步扫描异常'),
  CODE_OPERATE_FORMAT_FAIL: i18n_t('同步任务转换异常'),
  CODE_SCANNER_GET_ROOT_PATH_ERROR: i18n_t('同步获取配置信息异常'),
  CODE_WARN: i18n_t('同步异常'),
  CODE_PARAM_ERROR: i18n_t('同步任务参数异常'),
  CODE_CREATE_FOLDER_PARAM_ERROR: i18n_t('创建文件夹参数错误'),
  CODE_CREATE_FOLDER_ERROR: i18n_t('创建文件夹失败'),
  CODE_STOP_ERROR: i18n_t('停止错误'),
  CODE_DELETE_REMOTE_FILE_ERROR: i18n_t('删除远端文件错误'),
  CODE_DELETE_FOLDER_HAVE_FILE_ERROR: i18n_t('删除目录下存在文件'),
  CODE_REMOTE_REQUEST_FAIL: i18n_t('请求出错'),
  CODE_BASE_PARAM_ERROR: i18n_t('任务基础参数异常'),
  CODE_DIFF_LIST_ERROR: i18n_t('获取同步差异列表错误'),
  CODE_SSN_NO_MATCH: i18n_t('发现同名文件，稍后将自动重试'),
  CODE_DELETE_REMOTE_FOLDER_HAVE_FILE_ERROR: i18n_t('删除远端目录存在文件'),
  CODE_MOVE_LOCAL_SRC_NO_EXIST_ERROR: i18n_t('移动源路径不存在'),
  CODE_MOVE_LOCAL_CREATE_DIR_ERROR: i18n_t('移动创建目标路径失败'),
  CODE_DOWNLOAD_FILE_LOCAL_CHANGE: i18n_t('下载时本地文件发生变更'),
  CODE_DELETE_DIR_NO_EMPTY_ERROR: i18n_t('删除目录非空'),
  CODE_GET_LOCAL_PATH_INFO_ERROR: i18n_t('获取本地文件信息失败'),
  CODE_DOWNLOAD_GET_INFO_ERROR: i18n_t('下载获取云端文件信息失败'),
  CODE_DELETE_FILE_SIZE_CHANGE: i18n_t('删除文件时文件发生变更'),
  CODE_DELETE_FILE_MTIME_CHANGE: i18n_t('删除文件时文件修改时间发生变更'),
  CODE_DELETE_FILE_INODE_CHANGE: i18n_t('删除文件时文件发生变更'),
  CODE_NO_PERMISSION_ERROR: i18n_t('所含文件/文件夹缺少权限'),
  CODE_SYNC_TASK_NOT_FOUND_ERROR: i18n_t('同步任务不存在或者已被移除'),
  CODE_SCANNER_WARNING: i18n_t('扫描异常：连接不稳定，网络恢复后将自动继续同步'),
};

export function getErrorText(info: SyncStatusResultInfo) {
  if (info.error) {
    return i18n_t('同步错误：{slot1}', { slot1: info.error });
  }
  let text = ResultErrorTextMap[info.result];
  if (!text) {
    text = i18n_t('未知错误（{slot1}）', { slot1: info.result });
  }
  return text;
}

export enum CoSyncOperateStatus {
  TYPE_INIT = 'TYPE_INIT', // 初始化
  TYPE_WAILTING = 'TYPE_WAILTING', // 等待
  TYPE_START = 'TYPE_START', // 任务开始
  TYPE_HASH_COMPUTE = 'TYPE_HASH_COMPUTE', // hash计算
  TYPE_RUNNING = 'TYPE_RUNNING', // 运行
  TYPE_FINISH = 'TYPE_FINISH', // 完成
  TYPE_STOP = 'TYPE_STOP', // 停止
  TYPE_UPDATE_TREE = 'TYPE_UPDATE_TREE', // 更新同步树
  TYPE_GROUP_FINISH = 'TYPE_GROUP_FINISH', // 执行组执行完成
  TYPE_OPERATE_FINISH = 'TYPE_OPERATE_FINISH', // 所有执行任务都完成
}

export interface ResultInfo {
  result: CoSyncResultCode;
  error: string;
}

export interface ResultNodeInfo {
  localNode?: LocalTreeNode;
  remoteNode?: RemoteTreeNode;
}

export interface TaskBaseInfo {
  syncId: number;
  status: CoSyncOperateStatus;
  taskId: number;
  name: string;
  operation: SyncOperationType;
  relativePath: string;
  isDir?: boolean;

  finishTaskCount?: number;
  allTaskCount?: number;
  exceptionCount?: number;
}

export interface OperateTaskResultInfo extends ResultInfo, TaskBaseInfo {
  relativePath: string;
  originRelativePath: string;
  nodeInfo?: ResultNodeInfo;
  dontUpdateSyncTree?: boolean; // 该操作是否需要更新syncTree, true为不需要更新，其他则需要
  conflictResolveChoose?: conflictResolveType; // 若有冲突解决选择，则该值为选择的值，否则为undefind
}

export interface OperateTaskFinishInfo extends OperateTaskResultInfo {
  status: CoSyncOperateStatus.TYPE_FINISH;
  progress?: number; // 进度0到1
  finishTime: number; // 完成时间，毫秒
  runTime?: number; // 执行时间
  concurrent: boolean; // 是否并发 true为允许，则失败场景允许继续同步
}

export interface OperateTaskRunningInfo extends TaskBaseInfo {
  status: CoSyncOperateStatus.TYPE_RUNNING;
  progress: number; // 进度0到1
  speed: number; // 速率，单位为 byte/s
  loadedSize: number; // 已上传或者已下载的大小
  totalSize: number; // 上传或者下载的文件的大小
}

export interface OperateTaskHashComputeInfo extends TaskBaseInfo {
  status: CoSyncOperateStatus.TYPE_HASH_COMPUTE;
  progress: number; // 进度0到1
}

export interface OperateTaskStartInfo extends TaskBaseInfo {
  status: CoSyncOperateStatus.TYPE_START;
}

export interface OperateUpdateTreeInfo extends OperateTaskResultInfo {
  status: CoSyncOperateStatus.TYPE_UPDATE_TREE;
}

export interface OperateGroupResultInfo {
  status: CoSyncOperateStatus.TYPE_GROUP_FINISH;
  taskId: 0;

  finishTaskCount?: number;
  allTaskCount?: number;
  exceptionCount?: number;
}

export interface OperateResultInfo extends ResultInfo {
  status: CoSyncOperateStatus.TYPE_OPERATE_FINISH;
  taskId: 0;
  errorTaskNum: number;

  finishTaskCount?: number;
  allTaskCount?: number;
  exceptionCount?: number;
}

export interface OperateTaskStopInfo extends TaskBaseInfo {
  status: CoSyncOperateStatus.TYPE_STOP;
  progress?: number; // 进度0到1
}

export type OperateStatusInfo =
  | OperateTaskStartInfo
  | OperateTaskRunningInfo
  | OperateTaskFinishInfo
  | OperateUpdateTreeInfo
  | OperateGroupResultInfo
  | OperateResultInfo
  | OperateTaskStopInfo
  | OperateTaskHashComputeInfo;
